var _defineProperty = require("@babel/runtime/helpers/defineProperty");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const _require = require('lodash'),
  isPlainObject = _require.isPlainObject;
const vm = require('vm-shim');
const BehaviorError = require('./error/behaviorError');
const fakeRequireMap = require('./fakeRequireMap');
const _require2 = require('./profile'),
  profile = _require2.profile;

/**
 * @function handleError - processes an error and passes it to the native handler
 * @param {*} error - the error object
 * @param {*} nativeFunctions - contains the native error handler
 */
const handleError = (errorIn, nativeFunctions) => {
  // Handles conversion to and from platform-neutral errors
  // which are plain objects with
  // human-readable `message` and machine-readable `statusCode`.

  // eslint-disable-next-line no-console
  console.log("Behavior Error ".concat(JSON.stringify(errorIn, null, 2), "\n  ").concat(errorIn.stack));
  if (nativeFunctions === undefined || nativeFunctions.errorHandler === undefined) {
    let errorObject;
    if (isPlainObject(errorIn)) {
      const message = "".concat(errorIn.statusCode, " ").concat(errorIn.message);
      errorObject = new BehaviorError(message);
    } else {
      errorObject = errorIn;
    }
    throw errorObject;
  } else {
    let plainObject;
    if (isPlainObject(errorIn)) {
      plainObject = errorIn;
    } else {
      const message = "".concat(errorIn.message, "\n      ").concat(errorIn.debugMessage ? errorIn.debugMessage : '');
      plainObject = {
        message,
        statusCode: 500,
        stack: errorIn.stack
      };
    }
    nativeFunctions.errorHandler(plainObject);
  }
};
const behaviorExists = (behaviorName, nativeFunctions) => {
  try {
    // eslint-disable-next-line no-use-before-define
    if (getBehavior({
      type: behaviorName
    }, nativeFunctions) !== undefined) {
      return true;
    }
  } catch (err) {
    return nativeFunctions !== undefined && nativeFunctions.behaviorExists !== undefined && nativeFunctions.behaviorExists(behaviorName);
  }
  return false;
};

/**
 * @function evaluateCommon - calls the shared evaluator for this behavior spec.
 * @param {*} source - the source object
 * @param {*} content - the whole content
 * @param {*} parentContentPath - the content path of this behavior
 * @param {*} nativeFunctions - client-specific implementations
 * @returns {*} - the evaluated value
 */
const evaluateCommon = async (source, content, parentContentPath, transientContext, nativeFunctions, sourceEvaluator) => {
  // eslint-disable-next-line no-use-before-define
  const module = getBehavior(source, nativeFunctions);
  const evaluate = module.evaluate;
  const result = await evaluate(source, content, parentContentPath, transientContext, nativeFunctions, sourceEvaluator);
  return result;
};

/**
 * @function evaluateNative - calls the native evaluator for this behavior spec.
 * @param {*} source - the source object
 * @param {*} content - the whole content
 * @param {*} parentContentPath - the content path of this behavior
 * @param {*} nativeFunctions - client-specific implementations
 * @returns {*} - the evaluated value
 *
 * Note that nativeFunctions.evaluator has a signature that expects the source
 * and a "node style" callback.  this means the signature of that callback is:
 * ( error, value ), so if there is a problem, you should return that error
 * as the first param to the callback and you can ignore the second, while if
 * there is no error, you will call with null as the first parameter, and
 * return the valid value as the second.
 *
 * the ipad needed a callback based interaction, so the code below maps
 * from a callback interface to a promise interface.
 */
const evaluateNative = (source, content, parentContentPath, transientContext, nativeFunctions, sourceEvaluator) => {
  if (nativeFunctions === undefined || nativeFunctions.evaluator === undefined) {
    const err = new BehaviorError('undefined nativeFunctions.evaluator');
    err.statusCode = 404;
    throw err;
  }
  const result = new Promise((resolve, reject) => {
    const callback = (err, value) => {
      if (err !== null) {
        return reject(err);
      }
      return resolve(value);
    };
    nativeFunctions.evaluator(source, content, parentContentPath, transientContext, nativeFunctions, callback, sourceEvaluator);
  });
  return result;
};
const updateTransientContext = function updateTransientContext(context) {
  let map = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  let values = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  // `map` provides a mapping through which keys from `values` are stored in `context`.
  // similar to `name` in `array.map((name) => { /* do something with `name` */ });`
  Object.keys(map).forEach(key => {
    context[map[key]] = values[key];
  });
};

/**
 * @function evaluateSource - evaluates a source object
 * @param {*} source - the source object
 * @param {*} content - the whole content
 * @param {*} parentContentPath - the content path of this behavior
 * @param {*} nativeFunctions - client-specific implementations
 * @returns {*} - the evaluated value
 */

// `evaluateSource` and `evaluateVariables` are co-recursive
// so we declare `evaluateVariables` here and define it after `evaluateSource`.
let evaluateVariables;
const evaluateSource = async (source, content, parentContentPath, transientContext, nativeFunctions) => {
  if (Array.isArray(source)) {
    const results = [];
    for (let i = 0; i < source.length; i += 1) {
      /* eslint-disable-next-line no-await-in-loop */
      const result = await evaluateSource(source[i], content, parentContentPath, transientContext, nativeFunctions);
      results.push(result);
    }
    return results;
  }
  if (!isPlainObject(source) || source.type === undefined) {
    return source;
  }
  let result;

  // any behavior can have `variables` defined on it.  These vars
  // are read in and put into transientContext for use in
  // any nested behavior below the current behavior and
  // including the current behavior.

  const variables = await evaluateVariables(source.variables, content, parentContentPath, transientContext, nativeFunctions);
  const sourceEvaluator = async argformat => evaluateSource(argformat, content, parentContentPath, variables, nativeFunctions);
  try {
    result = await evaluateCommon(source, content, parentContentPath, variables, nativeFunctions, sourceEvaluator);
  } catch (commonError) {
    if (commonError.code === 'MODULE_NOT_FOUND') {
      try {
        result = await evaluateNative(source, content, parentContentPath, variables, nativeFunctions, sourceEvaluator);
      } catch (nativeError) {
        if (nativeError.statusCode === 404) {
          const notFoundError = {
            statusCode: nativeError.statusCode,
            message: "Behavior not found: ".concat(JSON.stringify(source.type))
          };
          handleError(notFoundError, nativeFunctions);
        } else {
          handleError(nativeError, nativeFunctions);
        }
      }
    } else {
      // console.log('error ' + commonError.message)
      handleError(commonError, nativeFunctions);
    }
  }
  if (source && source.defaultValue !== undefined && result === undefined) {
    result = await evaluateSource(source.defaultValue, content, parentContentPath, variables, nativeFunctions);
  }
  if (source.debug !== undefined) {
    // eslint-disable-next-line no-console
    console.log("DEBUG\n".concat(source.type, ": ").concat(source.debug, " = ").concat(JSON.stringify(result, null, 2), "\nformat: ").concat(JSON.stringify(source, null, 2), "\nvariables: ").concat(JSON.stringify(variables, null, 2), "\norigin: ").concat(source.origin, "\n    "));
  }
  return result;
};
const evaluateSources = async (sources, content, parentContentPath, transientContext, nativeFunctions) => {
  const evaluateSourcesProf = profile('evaluateSources');
  const results = {};
  const keys = Object.keys(sources);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    // eslint-disable-next-line no-await-in-loop
    results[key] = await evaluateSource(sources[key], content, parentContentPath, transientContext, nativeFunctions);
  }
  evaluateSourcesProf.end();
  return results;
};
evaluateVariables = async (newVariables, content, parentContentPath, existingVariables, nativeFunctions) => {
  const variables = _objectSpread({}, existingVariables);
  if (newVariables === undefined) {
    return variables;
  }

  // variables can be an object, or an array of objects.
  // if an array of objects, we evaluate the first object's
  // vars, then include those when evaluating the second
  // object's vars, etc.
  const variableBlocks = isPlainObject(newVariables) ? [newVariables] : newVariables;
  for (let i = 0; i < variableBlocks.length; i += 1) {
    const variableBlock = variableBlocks[i];
    const variableNames = Object.keys(variableBlock);
    for (let j = 0; j < variableNames.length; j += 1) {
      const variableName = variableNames[j];
      // eslint-disable-next-line no-await-in-loop
      const value = await evaluateSource(variableBlock[variableName], content, parentContentPath, variables, nativeFunctions);
      variables[variableName] = value;
    }
  }
  return variables;
};
const combForSource = async (source, content, parentContentPath, transientContext, nativeFunctions) => {
  const equals = await evaluateSource(source.equals, content, parentContentPath, transientContext, nativeFunctions);
  const doesNotEqual = await evaluateSource(source.doesNotEqual, content, parentContentPath, transientContext, nativeFunctions);
  let comb;
  if (equals !== undefined) {
    comb = item => item === equals;
  } else if (doesNotEqual !== undefined) {
    comb = item => item !== doesNotEqual;
  } else {
    comb = item => item !== undefined;
  }
  return comb;
};
const behaviors = {};
let configBehaviorCache = {};
const clearBehaviorCache = db => {
  if (db !== undefined) {
    // server clears cache based on customer db.
    delete configBehaviorCache[db];
  } else {
    // clients are always using only ONE db so they do not keep
    // track of it in many areas of code.  Thus, we only have a single
    // cache in behaviors in that case, so we can clear the entire
    // cache if db is undefined.
    configBehaviorCache = {};
  }
};

// default arg for testing purposes.
const testTmp = {
  db: 'tmp',
  configBehaviors: () => ({})
};
const convertCodeString = codeString => {
  if (codeString !== undefined) {
    // At the top of our javascript behaviors, we generally write:
    //   const { evaluateSource } = require('../lib/evaluateSource');
    // so that we can evaluate any behavioral parameters passed.
    // But because (on iPad at least) we don't actually have access
    // to the filesystem (everything is within a webpack) we need to
    // fake it here. To do this we run the codeString in a context
    // defining `require` as an object containing the `evaluateSource`
    // that we ourselves have `require`d at the top of this file.

    const getBasename = name => {
      const slashIndex = name.lastIndexOf('/');
      if (slashIndex > 0) {
        return name.substring(slashIndex + 1);
      }
      return name;
    };
    const fakeRequire = name => {
      const basename = getBasename(name);
      const localRequireMap = {
        evaluateSource: {
          evaluateSource,
          // eslint-disable-next-line no-use-before-define
          getBehavior,
          // exported for usage when loading template function modules
          updateTransientContext
        }
      };
      const dummy = () => {};
      return fakeRequireMap[basename] || localRequireMap[basename] || dummy;
    };
    const context = {
      require: fakeRequire,
      module: {}
    };
    const env = vm.runInNewContext(codeString, context);
    //  to cache these values, we need to get the db.  Most likely scenario
    // for that is to store it in the nativeFunctions - we should have it there, but will
    // require changes to server, web and ipad.
    if (env.module && env.module.exports) {
      // simple string compilation results in a env.module.exports.evaluate function
      return env.module.exports;
    }
    // webpack bundle compilation results in a env.global.evaluate function
    return env.global;
  }
  return undefined;
};

/**
 * This function tries to retrieve a common behavior module.  throws an
 * error if it does not exist.
 */
const getBehavior = function getBehavior(format) {
  let nativeFunctions = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : testTmp;
  const type = format.type;
  try {
    const db = nativeFunctions.db || 'tmp';
    if (configBehaviorCache[db] === undefined) {
      const configBehaviorsFunc = nativeFunctions.configBehaviors;
      const configBehaviors = configBehaviorsFunc === undefined ? {} : configBehaviorsFunc();
      configBehaviorCache[db] = {};
      Object.keys(configBehaviors).forEach(key => {
        const codeString = configBehaviors[key];
        configBehaviorCache[db][key] = convertCodeString(codeString);
      });
    }
    if (configBehaviorCache[db] !== undefined && configBehaviorCache[db][type] !== undefined) {
      return configBehaviorCache[db][type];
    }
  } catch (err) {
    // fallthrough on error
    // eslint-disable-next-line no-console
    console.warn("config behavior error ".concat(err.message, " ").concat(err.stack));
  }

  // check out local behaviors cache.
  if (behaviors[type] !== undefined) {
    return behaviors[type];
  }

  // eslint-disable-next-line import/no-dynamic-require, global-require
  const module = require("../behaviors/".concat(type));
  behaviors[type] = module;
  return module;
};
module.exports = {
  behaviorExists,
  clearBehaviorCache,
  combForSource,
  evaluateSource,
  evaluateSources,
  evaluateVariables,
  getBehavior,
  // exported for usage when loading template function modules
  handleError,
  updateTransientContext
};